import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { API, graphqlOperation } from "aws-amplify";
import Table from "./table/Table";
import Loading from "_shared/components/Loading/Loading";
import { usdCurrency } from "_shared/helpers/formatting";

//Table styles
const Styles = styled.div`
  table {
    border-collapse: collapse;
    height: calc(100vh - 320px);
    white-space: nowrap;
  }
  td {
    color: #ffffff;
    padding: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
  td:nth-child(1),
  td:nth-child(2),
  td:nth-child(3) {
    text-align: left;
  }
  table::-webkit-scrollbar {
    height: 15px;
    width: 15px;
    position: fixed;
    background-color: #999999;
    border: 4px solid #2e2e2e;
  }

  table::-webkit-scrollbar-thumb {
    height: 15px;
    border-radius: 10px;
    background-color: #ffffff;
  }
  .pagination {
    color: #ffffff;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
  }
  th {
    transition: background-color 0.25s ease, color 0.25s ease;
    margin-right: -12px;
    text-align: center;
  }
  th[draggable="true"]:hover {
    background-color: #666666 !important;
    color: #ffffff !important;
  }
  tr:nth-child(odd) {
    background-color: #131313;
  }
`;

//Table goes here
function DashboardTable(props) {
  const [refreshKiss, setRefreshKiss] = React.useState(false);
  const [columns, setColumns] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const fetchIdRef = React.useRef(0);

  const swapColumns = (parsedColumns) => {
    //Swap Range View and Range Low
    const tradingRangeIndex = parsedColumns.findIndex(({ Header }) =>
      Header.startsWith("Trading Range"),
    );
    const rangeViewIndex = parsedColumns[tradingRangeIndex]?.columns?.findIndex(
      ({ Header }) => Header === "Range",
    );
    const rangeLowIndex = parsedColumns[tradingRangeIndex]?.columns?.findIndex(
      ({ Header }) => Header === "Low",
    );
    if (rangeViewIndex < rangeLowIndex) {
      const rangeViewSection =
        parsedColumns[tradingRangeIndex]?.columns[rangeViewIndex];
      const rangeLowSection =
        parsedColumns[tradingRangeIndex]?.columns[rangeLowIndex];
      parsedColumns[tradingRangeIndex].columns[rangeViewIndex] =
        rangeLowSection;
      parsedColumns[tradingRangeIndex].columns[rangeLowIndex] =
        rangeViewSection;
    }
    return parsedColumns;
  };

  //Distinguish between KISS view columns and 'regular' view columns.
  useEffect(() => {
    // setTimeout(() => {
    if (localStorage.getItem("view") === "intermediate") {
      const parsedColumns = props.data.section
        .filter((obj) => obj.hidden === false && obj.kiss)
        .map((obj) => {
          return {
            Header: obj.name,
            columns: obj.column
              .filter((obj2) => obj2.hidden === false)
              .map((obj3) => {
                return {
                  id: obj3.id,
                  Header: obj3.name,
                  accessor: obj3.key,
                  Key: obj3.key,
                  Hidden: obj3.hidden,
                };
              }),
          };
        });
      if (parsedColumns.length === 0) {
        const intermediateColumns = [
          {
            Header: "Position",
            columns: [
              {
                id: "ticker",
                accessor: "ticker",
                Header: "Ticker",
              },
              {
                id: "px_close_1d",
                accessor: "px_close_1d",
                Header: "Closing Price",
              },
              {
                id: "px_last",
                accessor: "px_last",
                Header: "Last Price",
              },
            ],
          },
          {
            Header: "Trading Range",
            columns: [
              {
                id: "calc_trade_range_low",
                Key: "calc_trade_range_low",
                Header: "Low",
              },
              {
                id: "calc_trade_range_spread",
                Key: "calc_trade_range_spread",
                Header: "Range",
              },
              {
                id: "calc_trade_range_high",
                Key: "calc_trade_range_high",
                Header: "High",
              },
              {
                id: "calc_trade_range_odds",
                Key: "calc_trade_range_odds",
                Header: "Odds",
              },
              {
                id: "calc_trade_range_range_signal",
                Key: "calc_trade_range_range_signal",
                Header: "Range Signal",
              },
            ],
          },
          {
            Header: "Momentum Signals",
            columns: [
              {
                id: "calc_kiss_30d_momentum",
                Key: "calc_kiss_30d_momentum",
                Header: "ST Momentum",
              },
              {
                id: "calc_kiss_30d_3d_momentum",
                Key: "calc_kiss_30d_3d_momentum",
                Header: "-3D ST 3D Momentum",
              },
              {
                id: "calc_kiss_90d_momentum",
                Key: "calc_kiss_90d_momentum",
                Header: "MT Momentum",
              },
            ],
          },
          {
            Header: "Core Signal",
            columns: [
              {
                id: "calc_position_longbow_signal",
                Key: "calc_position_longbow_signal",
                Header: "Signal",
              },
              {
                id: "calc_position_longbow_signal_strength",
                Key: "calc_position_longbow_signal_strength",
                Header: "Signal Strength",
              },
            ],
          },
          {
            Header: "Trading Range Dynamics",
            columns: [
              {
                id: "calc_kiss_asymmetry",
                Key: "calc_kiss_asymmetry",
                Header: "Asymmetry",
              },
              {
                id: "calc_kiss_low_end",
                Key: "calc_kiss_low_end",
                Header: "Low End",
              },
              {
                id: "calc_kiss_high_end",
                Key: "calc_kiss_high_end",
                Header: "High End",
              },
            ],
          },
          {
            Header: "Implied Volatility",
            columns: [
              {
                id: "calc_kiss_30d_ivol_premium",
                Key: "calc_kiss_30d_ivol_premium",
                Header: "<30D IVOL Premium",
              },
              {
                id: "calc_kiss_30d_1w_ivol_premium",
                Key: "calc_kiss_30d_1w_ivol_premium",
                Header: "<30D 1W IVOL Premium",
              },
              {
                id: "calc_kiss_90d_ivol_premium",
                Key: "calc_kiss_90d_ivol_premium",
                Header: ">90D IVOL / Premium",
              },
            ],
          },
        ];
        setColumns(intermediateColumns);
      } else {
        setColumns(swapColumns(parsedColumns));
      }
    } else if (localStorage.getItem("view") === "basic") {
      const basicColumns = [
        {
          Header: "Position",
          columns: [
            {
              id: "ticker",
              accessor: "ticker",
              Header: "Ticker",
            },
            {
              id: "px_last",
              accessor: "px_last",
              Header: "Last Price",
            },
          ],
        },
        {
          Header: "Trading Range",
          columns: [
            {
              id: "calc_trade_range_low",
              accessor: "calc_trade_range_low",
              Header: "Low",
            },
            {
              id: "calc_trade_range_spread",
              accessor: "calc_trade_range_spread",
              Header: "Range",
            },
            {
              id: "calc_trade_range_high",
              accessor: "calc_trade_range_high",
              Header: "High",
            },
            {
              id: "calc_trade_range_range_signal",
              accessor: "calc_trade_range_range_signal",
              Header: "Range Signal",
            },
          ],
        },
        {
          Header: "Momentum Indicator",
          columns: [
            {
              id: "calc_momentum_indicator_trade_30d_flag",
              accessor: "calc_momentum_indicator_trade_30d_flag",
              Header: "S/T Momentum Signal",
            },
            {
              id: "calc_momentum_indicator_trade_90d_flag",
              accessor: "calc_momentum_indicator_trade_90d_flag",
              Header: "M/T Momentum Signal",
            },
          ],
        },
        {
          Header: "Volatility",
          columns: [
            {
              id: "calc_volatility_signal",
              accessor: "calc_volatility_signal",
              Header: "Premium | Signal",
            },
          ],
        },
        {
          Header: "Short Interest",
          columns: [
            {
              id: "si_signal",
              accessor: "si_signal",
              Header: "SI Signal",
            },
          ],
        },
      ];
      setColumns(basicColumns);
    } else if (
      localStorage.getItem("view") === "advanced" &&
      props?.data?.name === "42 Macro" &&
      props?.data?.longbow_panel
    ) {
      const macroColumns = [
        {
          Header: "Position",
          columns: [
            {
              Header: "Description",
              accessor: "name",
              Key: "name",
            },
            {
              Header: "Ticker",
              accessor: "ticker",
              Key: "ticker",
            },
            {
              Header: "Closing Price",
              accessor: "px_close_1d",
              Key: "px_close_1d",
            },
            {
              Header: "Last Price",
              accessor: "px_last",
              Key: "px_last",
            },
            {
              Header: "Expsoure",
              accessor: "exposure",
              Key: "exposure",
            },
            {
              Header: "Entry Date",
              accessor: "entry_date",
              Key: "entry_date",
            },
            {
              Header: "Entry Price",
              accessor: "entry_price",
              Key: "entry_price",
            },
          ],
        },
        {
          Header: "Trading Range",
          columns: [
            {
              Header: "Low",
              accessor: "calc_trade_range_low",
              Key: "calc_trade_range_low",
            },
            {
              Header: "Range",
              accessor: "calc_trade_range_spread",
              Key: "calc_trade_range_spread",
            },
            {
              Header: "High",
              accessor: "calc_trade_range_high",
              Key: "calc_trade_range_high",
            },
            {
              Header: "Range Signal",
              accessor: "calc_trade_range_range_signal",
              Key: "calc_trade_range_range_signal",
            },
            {
              Header: "% from Trade Range | Low",
              accessor: "calc_trade_range_percent_low",
              Key: "calc_trade_range_percent_low",
            },
            {
              Header: "% from Trade Range | High",
              accessor: "calc_trade_range_percent_high",
              Key: "calc_trade_range_percent_high",
            },
            {
              Header: "Odds",
              accessor: "calc_trade_range_odds",
              Key: "calc_trade_range_odds",
            },
          ],
        },
        {
          Header: "Longbow Core Signal",
          columns: [
            {
              Header: "Signal",
              accessor: "calc_position_longbow_signal",
              Key: "calc_position_longbow_signal",
            },
            {
              Header: "Signal Strength",
              accessor: "calc_position_longbow_signal_strength",
              Key: "calc_position_longbow_signal_strength",
            },
          ],
        },
        {
          Header: "Momentum Indicator",
          columns: [
            {
              Header: "S/T Momentum Signal",
              accessor: "calc_momentum_indicator_trade_30d_flag",
              Key: "calc_momentum_indicator_trade_30d_flag",
            },
            {
              Header: "S/T Momentum Price",
              accessor: "calc_momentum_indicator_trade_30d",
              Key: "calc_momentum_indicator_trade_30d",
            },
            {
              Header: "M/T Momentum Signal",
              accessor: "calc_momentum_indicator_trade_90d_flag",
              Key: "calc_momentum_indicator_trade_90d_flag",
            },
            {
              Header: "M/T Momentum Price",
              accessor: "calc_momentum_indicator_trade_90d",
              Key: "calc_momentum_indicator_trade_90d",
            },
          ],
        },
        {
          Header: "Volatility",
          columns: [
            {
              Header: "Premium | Signal",
              accessor: "calc_volatility_signal",
              Key: "calc_volatility_signal",
            },
            {
              Header: "Premium | Current",
              accessor: "calc_volatility_current",
              Key: "calc_volatility_current",
            },
            {
              Header: "Premium | 1W",
              accessor: "calc_volatility_1w",
              Key: "calc_volatility_1w",
            },
            {
              Header: "Premium | ST <30D",
              accessor: "calc_volatility_premium_trade_30d",
              Key: "calc_volatility_premium_trade_30d",
            },
            {
              Header: "Premium | MT >90D",
              accessor: "calc_volatility_premium_trade_90d",
              Key: "calc_volatility_premium_trade_90d",
            },
            {
              Header: "Realized | Current",
              accessor: "calc_realized_volatility_current",
              Key: "calc_realized_volatility_current",
            },
            {
              Header: "Realized | 1W",
              accessor: "calc_realized_volatility_1w",
              Key: "calc_realized_volatility_1w",
            },
            {
              Header: "Realized | 1M",
              accessor: "calc_realized_volatility_1m",
              Key: "calc_realized_volatility_1m",
            },
            {
              Header: "Realized | 3M",
              accessor: "calc_realized_volatility_3m",
              Key: "calc_realized_volatility_3m",
            },
            {
              Header: "Beta",
              accessor: "eqy_beta",
              Key: "eqy_beta",
            },
          ],
        },
        {
          Header: "Price Returns",
          columns: [
            {
              Header: "1-Day / % Chg",
              accessor: "current_trr_1d",
              Key: "current_trr_1d",
            },
            {
              Header: "5-Day / % Chg",
              accessor: "current_trr_5d",
              Key: "current_trr_5d",
            },
            {
              Header: "1-Mth / % Chg",
              accessor: "current_trr_1mo",
              Key: "current_trr_1mo",
            },
            {
              Header: "3-Mth / % Chg",
              accessor: "current_trr_3mo",
              Key: "current_trr_3mo",
            },
            {
              Header: "YTD / % Chg",
              accessor: "current_trr_ytd",
              Key: "current_trr_ytd",
            },
            {
              Header: "ITD % Chg",
              accessor: "itd_pct_chg",
              Key: "itd_pct_chg",
            },
          ],
        },
        {
          Header: "Volume",
          columns: [
            {
              Header: "D/D / % Chg",
              accessor: "calc_volume_1d",
              Key: "calc_volume_1d",
            },
            {
              Header: "1W / Avg",
              accessor: "calc_volume_5d",
              Key: "calc_volume_5d",
            },
            {
              Header: "1M / Avg",
              accessor: "calc_volume_1m",
              Key: "calc_volume_1m",
            },
            {
              Header: "3M / Avg",
              accessor: "calc_volume_3m",
              Key: "calc_volume_3m",
            },
          ],
        },
        {
          Header: "Short Interest",
          columns: [
            {
              Header: "% Equity Float / Date",
              accessor: "si_percent_equity_float",
              Key: "si_percent_equity_float",
            },
          ],
        },
        {
          Header: "% Change in # Shares Short",
          columns: [
            {
              Header: "Vs. Last Period / Date",
              accessor: "short_int_pct_chg",
              Key: "short_int_pct_chg",
            },
            {
              Header: "Mth-Over-Mth / Date",
              accessor: "onem_short_int_pct",
              Key: "onem_short_int_pct",
            },
          ],
        },
      ];
      setColumns(macroColumns);
    } else if (
      localStorage.getItem("view") === "advanced" &&
      props?.data?.name === "Herb Greenberg" &&
      props?.data?.longbow_panel
    ) {
      const herbColumns = [
        {
          Header: "Position",
          columns: [
            {
              Header: "Description",
              accessor: "name",
              Key: "name",
            },
            {
              Header: "Ticker",
              accessor: "ticker",
              Key: "ticker",
            },
            {
              Header: "Closing Price",
              accessor: "px_close_1d",
              Key: "px_close_1d",
            },
            {
              Header: "Last Price",
              accessor: "px_last",
              Key: "px_last",
            },
            {
              Header: "Entry Date",
              accessor: "entry_date",
              Key: "entry_date",
            },
            {
              Header: "Buy\nSell",
              accessor: "entry_type",
              Key: "entry_type",
            },
            {
              Header: "Entry Price",
              accessor: "entry_price",
              Key: "entry_price",
            },
          ],
        },
        {
          Header: "Trading Range",
          columns: [
            {
              Header: "Low",
              accessor: "calc_trade_range_low",
              Key: "calc_trade_range_low",
            },
            {
              Header: "Range",
              accessor: "calc_trade_range_spread",
              Key: "calc_trade_range_spread",
            },
            {
              Header: "High",
              accessor: "calc_trade_range_high",
              Key: "calc_trade_range_high",
            },
            {
              Header: "Range Signal",
              accessor: "calc_trade_range_range_signal",
              Key: "calc_trade_range_range_signal",
            },
            {
              Header: "% from Trade Range | Low",
              accessor: "calc_trade_range_percent_low",
              Key: "calc_trade_range_percent_low",
            },
            {
              Header: "% from Trade Range | High",
              accessor: "calc_trade_range_percent_high",
              Key: "calc_trade_range_percent_high",
            },
            {
              Header: "Odds",
              accessor: "calc_trade_range_odds",
              Key: "calc_trade_range_odds",
            },
          ],
        },
        {
          Header: "Longbow Core Signal",
          columns: [
            {
              Header: "Signal",
              accessor: "calc_position_longbow_signal",
              Key: "calc_position_longbow_signal",
            },
            {
              Header: "Signal Strength",
              accessor: "calc_position_longbow_signal_strength",
              Key: "calc_position_longbow_signal_strength",
            },
          ],
        },
        {
          Header: "Momentum Indicator",
          columns: [
            {
              Header: "S/T Momentum Signal",
              accessor: "calc_momentum_indicator_trade_30d_flag",
              Key: "calc_momentum_indicator_trade_30d_flag",
            },
            {
              Header: "S/T Momentum Price",
              accessor: "calc_momentum_indicator_trade_30d",
              Key: "calc_momentum_indicator_trade_30d",
            },
            {
              Header: "M/T Momentum Signal",
              accessor: "calc_momentum_indicator_trade_90d_flag",
              Key: "calc_momentum_indicator_trade_90d_flag",
            },
            {
              Header: "M/T Momentum Price",
              accessor: "calc_momentum_indicator_trade_90d",
              Key: "calc_momentum_indicator_trade_90d",
            },
          ],
        },
        {
          Header: "Volatility",
          columns: [
            {
              Header: "Premium | Signal",
              accessor: "calc_volatility_signal",
              Key: "calc_volatility_signal",
            },
            {
              Header: "Premium | Current",
              accessor: "calc_volatility_current",
              Key: "calc_volatility_current",
            },
            {
              Header: "Premium | 1W",
              accessor: "calc_volatility_1w",
              Key: "calc_volatility_1w",
            },
            {
              Header: "Premium | ST <30D",
              accessor: "calc_volatility_premium_trade_30d",
              Key: "calc_volatility_premium_trade_30d",
            },
            {
              Header: "Premium | MT >90D",
              accessor: "calc_volatility_premium_trade_90d",
              Key: "calc_volatility_premium_trade_90d",
            },
            {
              Header: "Realized | Current",
              accessor: "calc_realized_volatility_current",
              Key: "calc_realized_volatility_current",
            },
            {
              Header: "Realized | 1W",
              accessor: "calc_realized_volatility_1w",
              Key: "calc_realized_volatility_1w",
            },
            {
              Header: "Realized | 1M",
              accessor: "calc_realized_volatility_1m",
              Key: "calc_realized_volatility_1m",
            },
            {
              Header: "Realized | 3M",
              accessor: "calc_realized_volatility_3m",
              Key: "calc_realized_volatility_3m",
            },
            {
              Header: "Beta",
              accessor: "eqy_beta",
              Key: "eqy_beta",
            },
          ],
        },
        {
          Header: "Price Returns",
          columns: [
            {
              Header: "1-Day / % Chg",
              accessor: "current_trr_1d",
              Key: "current_trr_1d",
            },
            {
              Header: "5-Day / % Chg",
              accessor: "current_trr_5d",
              Key: "current_trr_5d",
            },
            {
              Header: "1-Mth / % Chg",
              accessor: "current_trr_1mo",
              Key: "current_trr_1mo",
            },
            {
              Header: "3-Mth / % Chg",
              accessor: "current_trr_3mo",
              Key: "current_trr_3mo",
            },
            {
              Header: "YTD / % Chg",
              accessor: "current_trr_ytd",
              Key: "current_trr_ytd",
            },
            {
              Header: "ITD % Chg",
              accessor: "itd_pct_chg",
              Key: "itd_pct_chg",
            },
          ],
        },
        {
          Header: "Volume",
          columns: [
            {
              Header: "D/D / % Chg",
              accessor: "calc_volume_1d",
              Key: "calc_volume_1d",
            },
            {
              Header: "1W / Avg",
              accessor: "calc_volume_5d",
              Key: "calc_volume_5d",
            },
            {
              Header: "1M / Avg",
              accessor: "calc_volume_1m",
              Key: "calc_volume_1m",
            },
            {
              Header: "3M / Avg",
              accessor: "calc_volume_3m",
              Key: "calc_volume_3m",
            },
          ],
        },
        {
          Header: "Short Interest",
          columns: [
            {
              Header: "% Equity Float / Date",
              accessor: "si_percent_equity_float",
              Key: "si_percent_equity_float",
            },
          ],
        },
        {
          Header: "% Change in # Shares Short",
          columns: [
            {
              Header: "Vs. Last Period / Date",
              accessor: "short_int_pct_chg",
              Key: "short_int_pct_chg",
            },
            {
              Header: "Mth-Over-Mth / Date",
              accessor: "onem_short_int_pct",
              Key: "onem_short_int_pct",
            },
          ],
        },
      ];
      setColumns(herbColumns);
    } else {
      const parsedColumns = props.data.section
        .filter((obj) => obj.hidden === false && !obj.kiss)
        .map((obj) => {
          return {
            Header: obj.name,
            columns: obj.column
              .filter((obj2) => obj2.hidden === false)
              .map((obj3) => {
                return {
                  id: obj3.id,
                  Header: obj3.name,
                  accessor: obj3.key,
                  Key: obj3.key,
                  Hidden: obj3.hidden,
                };
              }),
          };
        });
      setColumns(swapColumns(parsedColumns));
    }
    //Set which columns to hide in both views individually.
    const hiddenColumns = props.data.section
      .filter((obj) => obj.hidden === true)
      .map((obj) => {
        return {
          name: obj.name,
          id: obj.id,
          kiss: obj.kiss,
        };
      });
    setHiddenColumns(hiddenColumns);
    // }, 1);
    // eslint-disable-next-line
  }, [refreshKiss]);

  const fetchData = React.useCallback(
    async ({ tab, pageSize, pageIndex, sortBy }) => {
      const fetchId = ++fetchIdRef.current;

      if (fetchId === fetchIdRef.current) {
        setLoading(true);
        setRefreshKiss(new Date());
        if (sortBy) {
          if (sortBy.includes("calc_trade_range_range")) {
            sortBy = `algo_daily_CALC_TRADING_RANGE_SIGNAL:${
              sortBy.split(":")[1]
            }`;
          } else if (sortBy.includes("calc_trade_range_spread")) {
            sortBy = `algo_daily_CALC_TRADE_RANGE_ODDS:${sortBy.split(":")[1]}`;
          } else if (sortBy.includes("algo_daily_CALC_TRADE_RANGE_ODDS")) {
            sortBy = `algo_daily_CALC_TRADE_RANGE_PERCENT_LOW:${
              sortBy.split(":")[1]
            }`;
          } else if (sortBy.includes("calc_")) {
            sortBy = `algo_daily_${sortBy.split(":")[0].toUpperCase()}:${
              sortBy.split(":")[1]
            }`;
          }
        }
        let isDaily = true;
        const subscriptionCheck = await API.graphql(
          graphqlOperation(
            `{
            user_subscriptions(where: {_and: {subscription_id: {_ilike: "%_plan"}, frequency: {_eq: "daily"}}}) {
              id
            }
          }`,
          ),
        );
        if (subscriptionCheck.data.user_subscriptions.length === 0) {
          isDaily = false;
        }

        const response = await API.graphql(
          graphqlOperation(`{
                        ticker(where: {deleted: {_is_null: true}, panel_id: {_eq:"${
                          tab ? tab.id : ""
                        }"}}, order_by: {${
            sortBy ? `data: {${sortBy}}` : `order: asc`
          }}, limit: ${pageSize}, offset: ${pageSize * pageIndex}) {
                          id
                          ticker
                          alert_longbow_overall_signal
                          alert_trading_range_signal
                          alert_momentum_30d_flag
                          alert_momentum_90d_flag
                          alert_volatility_signal
                          data {
                            px_volume
                            px_volume_1d
                            volume_avg_5d
                            volume_avg_6m
                            volume_avg_3m
                            volume_avg_30d
                            volatility_30d
                            volatility_30d_1d
                            volatility_30d_1w
                            volatility_30d_1m
                            volatility_30d_3m
                            ticker_and_exch_code
                            ticker
                            si_percent_equity_float
                            short_int_pct_chg
                            security_typ
                            px_last
                            px_close_ytd
                            px_close_dt
                            px_close_5d
                            px_close_3m
                            px_close_1yr
                            px_close_1m
                            px_close_1d
                            put_imp_vol_30d
                            put_imp_vol_30d_1d
                            put_imp_vol_30d_1w
                            put_imp_vol_30d_1m
                            put_imp_vol_30d_3m
                            onem_short_int_pct
                            name
                            eqy_beta_6m: eqy_raw_beta_6m
                            eqy_beta: eqy_raw_beta
                            current_trr_ytd
                            current_trr_5d
                            current_trr_3mo
                            current_trr_1mo
                            current_trr_1d
                            crncy
                            country
                            cdr_country_code
                            short_int_date
                            red_px_last
                            red_eqy_beta: red_eqy_raw_beta
                            red_eqy_beta_6m: red_eqy_raw_beta_6m
                            red_si_percent_equity_float
                            red_short_int_pct_chg
                            red_onem_short_int_pct
                            red_volatility_30d
                            red_put_imp_vol_30d
                            red_current_trr_1d
                            red_current_trr_5d
                            red_current_trr_1mo
                            red_current_trr_3mo
                            red_current_trr_ytd
                            red_px_volume_1d
                            red_volume_avg_5d
                            red_volume_avg_30d
                            red_volume_avg_3m
                            red_volume_avg_6m
                            red_px_close_1d
                            red_px_close_5d
                            red_px_close_1m
                            red_px_close_3m
                            red_px_close_1yr
                            red_px_close_ytd
                            calc_position_longbow_signal_strength
                            insider_monthly_transaction
                            algo_30D_ST_MOMENTUM
                            algo_30D_ST_MOMENTUM_3D
                            algo_30D_ST_PRICE
                            algo_90D_MT_MOMENTUM
                            algo_90D_MT_PRICE
                            algo_IVOL_SIGNAL
                            algo_CALC_TRADING_RANGE_SIGNAL
                            algo_CALC_TRADING_RANGE_ASYMMETRY
                            algo_CALC_TRADE_RANGE_LOW
                            algo_CALC_TRADE_RANGE_HIGH
                            algo_IVOL_1WK_CHECK
                            algo_CALC_TRADE_RANGE_PERCENT_LOW
                            algo_CALC_TRADE_RANGE_PERCENT_HIGH
                            algo_CALC_POSITION_LONGBOW_SIGNAL
                            algo_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH
                            algo_CALC_TRADE_RANGE_ODDS
                            algo_CurrImpVolDiscPrem
                            algo_CurrImpVolDiscPrem1D
                            algo_CurrImpVolDiscPrem30D
                            algo_CurrImpVolDiscPrem7D
                            algo_CurrImpVolDiscPrem90D
                            algo_daily_30D_ST_MOMENTUM
                            algo_daily_30D_ST_MOMENTUM_3D
                            algo_daily_30D_ST_PRICE
                            algo_daily_90D_MT_MOMENTUM
                            algo_daily_90D_MT_PRICE
                            algo_daily_IVOL_SIGNAL
                            algo_daily_CALC_TRADING_RANGE_SIGNAL
                            algo_daily_CALC_TRADING_RANGE_ASYMMETRY
                            algo_daily_CALC_TRADE_RANGE_LOW
                            algo_daily_CALC_TRADE_RANGE_HIGH
                            algo_daily_IVOL_1WK_CHECK
                            algo_daily_IVOL_3MO_CHECK
                            algo_daily_CALC_TRADE_RANGE_PERCENT_LOW
                            algo_daily_CALC_TRADE_RANGE_PERCENT_HIGH
                            algo_daily_CALC_POSITION_LONGBOW_SIGNAL
                            algo_daily_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH
                            algo_daily_CALC_TRADE_RANGE_ODDS
                            algo_daily_CurrImpVolDiscPrem
                            algo_daily_CurrImpVolDiscPrem1D
                            algo_daily_CurrImpVolDiscPrem30D
                            algo_daily_CurrImpVolDiscPrem7D
                            algo_daily_CurrImpVolDiscPrem90D
                            calc_realized_volatility_current
                            calc_realized_volatility_1w
                            calc_realized_volatility_1m
                            calc_realized_volatility_3m
                            exposure
                            entry_date
                            entry_type
                            entry_price
                            daily
                            country
                            algo_eod_CALC_POSITION_LONGBOW_SIGNAL
                            algo_eod_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH
                            algo_eod_CALC_TRADE_RANGE_ODDS
                            algo_eod_PX_LAST
                            algo_eod_CALC_TRADE_RANGE_LOW
                            algo_eod_CALC_TRADE_RANGE_HIGH
                            volume_avg_1d
                            calc_volume_1d
                            calc_volume_5d
                            calc_volume_1m
                            calc_volume_3m
                            algo_daily_kiss_low
                            algo_daily_kiss_high
                            eqy_split_dt
                            eqy_split_ratio
                          }
                        }
                      }`),
        );
        for (let i = 0; i < response.data.ticker.length; i++) {
          if (response.data.ticker[i].data) {
            response.data.ticker[i].data.id = response.data.ticker[i].id;

            //move alert objects to data
            response.data.ticker[i].data.alert_longbow_overall_signal =
              response.data.ticker[i].alert_longbow_overall_signal;
            response.data.ticker[i].data.alert_trading_range_signal =
              response.data.ticker[i].alert_trading_range_signal;
            response.data.ticker[i].data.alert_momentum_30d_flag =
              response.data.ticker[i].alert_momentum_30d_flag;
            response.data.ticker[i].data.alert_momentum_90d_flag =
              response.data.ticker[i].alert_momentum_90d_flag;
            response.data.ticker[i].data.alert_volatility_signal =
              response.data.ticker[i].alert_volatility_signal;

            response.data.ticker[i] = response.data.ticker[i].data;
            if (!response.data.ticker[i].security_typ) {
              response.data.ticker[i].security_typ = "US EQUITIES";
            }
          }
          //move algo columns to algo object
          response.data.ticker[i].algo = {
            data: {
              "30D_ST_MOMENTUM":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_30D_ST_MOMENTUM`
                ],
              // `30D_ST_MOMENTUM_3D`: response.data.ticker[i][`algo${isDaily?`_daily`:``}_30D_ST_MOMENTUM_3D`],
              "30D_ST_PRICE":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_30D_ST_PRICE`
                ],
              "90D_MT_MOMENTUM":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_90D_MT_MOMENTUM`
                ],
              "30D_ST_MOMENTUM_3D":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_30D_ST_MOMENTUM_3D`
                ],
              "90D_MT_PRICE":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_90D_MT_PRICE`
                ],
              IVOL_SIGNAL:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_IVOL_SIGNAL`
                ],
              CALC_TRADING_RANGE_SIGNAL:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADING_RANGE_SIGNAL`
                ],
              CALC_TRADING_RANGE_ASYMMETRY:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADING_RANGE_ASYMMETRY`
                ],
              CALC_TRADE_RANGE_LOW:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_LOW`
                ],
              CALC_TRADE_RANGE_HIGH:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_HIGH`
                ],
              IVOL_1WK_CHECK:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_IVOL_1WK_CHECK`
                ],
              IVOL_3MO_CHECK:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_IVOL_3MO_CHECK`
                ],
              CALC_TRADE_RANGE_PERCENT_LOW:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_PERCENT_LOW`
                ],
              CALC_TRADE_RANGE_PERCENT_HIGH:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_PERCENT_HIGH`
                ],
              CALC_POSITION_LONGBOW_SIGNAL:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_POSITION_LONGBOW_SIGNAL`
                ],
              CALC_POSITION_LONGBOW_SIGNAL_STRENGTH:
                response.data.ticker[i][
                  `algo${
                    isDaily ? `_daily` : ``
                  }_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH`
                ],
              CALC_TRADE_RANGE_ODDS:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_ODDS`
                ],
              CurrImpVolDiscPrem:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem`
                ],
              CurrImpVolDiscPrem1D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem1D`
                ],
              CurrImpVolDiscPrem7D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem7D`
                ],
              CurrImpVolDiscPrem30D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem30D`
                ],
              CurrImpVolDiscPrem90D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem90D`
                ],
            },
          };

          //hide imp vol for certain tickers
          if (
            [
              "POSH",
              "EWK",
              "AVLR",
              "BIL",
              "PBJ",
              "LQDH",
              "CHIH",
              "DEF",
              "NLR",
              "CHIE",
              "XTL",
              "BAB",
              "ICSH",
              "SEV",
              "DWNI",
              "DXJS",
              "IHY",
              "BIV",
              "NLS",
              "SLQD",
              "CCRV",
              "NVCR",
              "CTXS",
              "FNDX",
              "SHV",
              "CMF",
              "GRN",
              "VCLO",
              "HYGH",
              "RIGZ",
              "KEUA",
              "DBE",
              "EWL",
              "IGF",
              "QGEN",
              "GVIP",
              "MUB",
              "RUTH",
              "BWX",
              "VEGI",
              "CHIR",
              "BNDD",
              "BBBY",
              "OGI",
              "STIP",
              "NOMD",
              "BCO",
              "EQAL",
              "CAF",
              "BJK",
              "POWW",
              "EMLC",
              "CEW",
              "ERICB",
              "ODV",
              "SJB",
              "ORCC",
              "IDEX",
              "SYPR",
              "BKLN",
              "WRLD",
              "SJR/B",
              "BFI",
              "EFZ",
              "XHR",
              "AAL",
              "DBV",
              "BTF",
              "TCRT",
              "GERN",
              "CHIK",
              "BGFV",
              "LW",
              "ONEM",
              "IVR",
              "LXU",
              "KMLM",
              "EVBG",
              "EUM",
              "PSON",
              "SLY",
              "LNW",
            ].includes(response.data.ticker[i].ticker)
          ) {
            for (const key of [
              "algo_CurrImpVolDiscPrem",
              "algo_CurrImpVolDiscPrem1D",
              "algo_CurrImpVolDiscPrem7D",
              "algo_CurrImpVolDiscPrem30D",
              "algo_CurrImpVolDiscPrem90D",
            ]) {
              try {
                response.data.ticker[i][key] = null;
                response.data.ticker[i].algo.data[key.replace("algo_", "")] =
                  null;
              } catch (err) {}
            }
          }

          //replace asset classes for macro specifically
          if (["SPY", "QQQ", "IWM"].includes(response.data.ticker[i].ticker)) {
            response.data.ticker[i].security_typ = "US EQUITIES";
          } else if (
            ["EWG", "EWI", "EWJ"].includes(response.data.ticker[i].ticker)
          ) {
            response.data.ticker[i].security_typ = "DM EQUITIES";
          } else if (
            ["FXI", "RSX", "EEM"].includes(response.data.ticker[i].ticker)
          ) {
            response.data.ticker[i].security_typ = "EM EQUITIES";
          } else if (
            ["TLT", "LQD", "TIP"].includes(response.data.ticker[i].ticker)
          ) {
            response.data.ticker[i].security_typ = "FIXED INCOME";
          } else if (
            ["FXE", "FXY", "FXF"].includes(response.data.ticker[i].ticker)
          ) {
            response.data.ticker[i].security_typ = "CURRENCIES";
          } else if (
            ["GLD", "USO", "CPER"].includes(response.data.ticker[i].ticker)
          ) {
            response.data.ticker[i].security_typ = "COMMODITIES";
          }
        }
        for (const obj of response.data.ticker) {
          obj["red_calc_realized_volatility_current"] =
            obj["calc_realized_volatility_current"] < 0;
          obj["red_calc_realized_volatility_1w"] =
            obj["calc_realized_volatility_1w"] < 0;
          obj["red_calc_realized_volatility_1m"] =
            obj["calc_realized_volatility_1m"] < 0;
          obj["red_calc_realized_volatility_3m"] =
            obj["calc_realized_volatility_3m"] < 0;

          for (const key of Object.keys(obj)) {
            // if (["name"].includes(key)) {
            //   obj.name = obj.name.toLowerCase().replace(/\b./g, function (a) {
            //     return a.toUpperCase();
            //   });
            //   if (obj.name.indexOf(".Com")) {
            //     obj.name = obj.name.replace(".Com", ".com");
            //   }
            //   if (obj.name.indexOf("Etf")) {
            //     obj.name = obj.name.replace("Etf", "ETF");
            //   }
            // }
            if (
              [
                "px_last",
                "px_close_1d",
                "calc_trade_range_low",
                "calc_trade_range_high",
              ].includes(key) &&
              obj[key]
            ) {
              obj[key] = usdCurrency(obj[key]);
            }
            // if (['px_volume_1d', 'volume_avg_5d', 'volume_avg_30d', 'volume_avg_3m', 'volume_avg_6m'].includes(key) && obj[key]) {
            //     //it might be worth computing this in longbow_hapi in order to determine the red_volume_avg values
            //     obj[key] = `${(((obj.px_volume_1d-obj[key])/obj[key])*100).toFixed(3)}%`
            // }
            if (
              [
                "current_trr_1d",
                "current_trr_5d",
                "current_trr_1mo",
                "current_trr_3m",
                "current_trr_ytd",
                "si_percent_equity_float",
                "short_int_pct_chg",
                "onem_short_int_pct",
              ].includes(key) &&
              obj[key]
            ) {
              obj[key] = `${obj[key].toFixed(2)}%`;
            }
            if (
              [
                "calc_volume_1d",
                "calc_volume_5d",
                "calc_volume_1m",
                "calc_volume_3m",
              ].includes(key)
            ) {
              const computedValue = obj[key] * 100;
              obj[key] = `${computedValue.toFixed(2)}%`;
              obj[`red_${key}`] = computedValue < 0;
            }
            if (["eqy_beta"].includes(key)) {
              obj[key] = parseFloat(obj[key]).toFixed(2);
              if (obj[key] === "NaN") {
                obj[key] = null;
              }
            }
          }

          //do we have 42macro tickers? we might need to compute itd
          if (
            ["42 Macro", "Options Insight", "Herb Greenberg"].includes(tab.name)
          ) {
            obj.itd_pct_chg =
              (obj.px_last?.replace("$", "") / obj.entry_price - 1) * 100;

            if (tab.name === "Herb Greenberg" && obj.entry_type === "Sell") {
              obj.itd_pct_chg *= -1;
            }
          }

          //fix percent change
          try {
            const pxLast = parseFloat(obj["px_last"]);
            const pxClose1d = parseFloat(obj["px_close_1d"]);
            obj["current_trr_1d"] = `${((pxLast / pxClose1d - 1) * 100).toFixed(
              2,
            )}%`;
          } catch (err) {
            console.log(err);
          }

          //fix "Last Price" and "% Chg" coloration
          try {
            const pxLast = parseFloat(obj["px_last"]);
            const pxClose1d = parseFloat(obj["px_close_1d"]);
            const currentTrr1d = parseFloat(
              obj["current_trr_1d"]?.slice(0, -1),
            );
            obj["red_px_last"] =
              pxLast > pxClose1d ? false : pxLast === pxClose1d ? null : true;
            if (currentTrr1d < 0 && !obj["red_px_last"]) {
              obj["current_trr_1d"] = obj["current_trr_1d"];
            }
          } catch (err) {
            console.log(err);
          }
        }

        let count = await API.graphql(
          graphqlOperation(`{
                    ticker_aggregate {
                      aggregate {
                        count
                      }
                    }
                  }`),
        );

        if (tab.name === "Macro ETFs") {
          const subscriptionCheck = await API.graphql(
            graphqlOperation(
              `{user_subscriptions(where: {subscription_id: {_in: ["basic_plan","free_plan"]}}) {id}}`,
            ),
          );
          if (subscriptionCheck.data.user_subscriptions.length > 0) {
            response.data.ticker = response.data.ticker.filter((obj) =>
              [
                "SPY US Equity",
                "QQQ US Equity",
                "IWM US Equity",
                "EFA US Equity",
                "EWG US Equity",
                "EWJ US Equity",
                "FXI US Equity",
                "RSX US Equity",
                "EEM US Equity",
                "INDA US Equity",
              ].includes(obj.ticker),
            );
            count = {
              data: { ticker_aggregate: { aggregate: { count: 20 } } },
            };
          }
        }

        setPageCount(
          Math.ceil(count.data.ticker_aggregate.aggregate.count / pageSize),
        );
        setLoading(false);

        // setData(response.data.ticker.reverse());

        // In the event that we need to manually sort the range view
        if (
          sortBy?.includes("algo_daily_CALC_TRADE_RANGE_ODDS") ||
          sortBy?.includes("calc_trade_range_spread")
        ) {
          const [asc, desc] =
            sortBy.split(":")[1] === " desc" ? [1, -1] : [-1, 1];
          const calcOdds = (ticker) => {
            const low =
              ((ticker.px_last - ticker.algo_daily_CALC_TRADE_RANGE_LOW) /
                ticker.px_last) *
              -1;
            const high =
              (ticker.px_last - ticker.algo_daily_CALC_TRADE_RANGE_HIGH) /
              ticker.px_last;
            const result = low / (low + high);
            return result;
          };
          setData(
            response.data.ticker.sort((a, b) =>
              calcOdds(a) < calcOdds(b) ? asc : desc,
            ),
          );
        } else {
          setData(response.data.ticker.reverse());
        }
      }
    },
    [],
  );

  const setRecords = React.useCallback(async (newData) => {
    setData(newData);
  }, []);

  React.useEffect(() => {
    fetchData({ tab: props.data, pageSize: 250, pageIndex: 0 });
  }, [props.data, fetchData]);

  return (
    <div>
      <div className="dashboard-content">
        <Styles>
          <Table
            columns={columns}
            data={data}
            fetchData={fetchData}
            loading={loading}
            pageCount={pageCount}
            props={props}
            setRecords={setRecords}
            setRefreshKiss={setRefreshKiss}
            kiss={props.kiss}
            planInterval={props.planInterval}
            setPlanInterval={props.setPlanInterval}
            alertsInterval={props.alertsInterval}
            setLoading={setLoading}
          />
          {
            <div
              style={{
                padding: 0,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                fontSize: 12,
              }}
            >
              {hiddenColumns.length > 0 && (
                <div
                  style={{
                    color: "#75CE41",
                    padding: 10,
                    textAlign: "center",
                    width: 125,
                    fontWeight: "bold",
                  }}
                >
                  SEE MORE:
                </div>
              )}
              {hiddenColumns.map((obj) => (
                <div
                  onClick={async () => {
                    setLoading(true);
                    await API.graphql(
                      graphqlOperation(
                        `mutation {update_section_by_pk(pk_columns: {id: "${obj.id}"}, _set: {hidden: false}) {id}}`,
                      ),
                    );
                    //refresh the table
                    props.fetchPanels(false);
                  }}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "#ffffff",
                    padding: 5,
                    textAlign: "center",
                    borderStyle: "solid",
                    borderColor: "#ffffff",
                    borderWidth: 1,
                    textTransform: "uppercase",
                    width: 200,
                    marginRight: 20,
                  }}
                  key={obj.id}
                >
                  <div
                    style={{
                      marginLeft: 10,
                      textAlign: "center",
                      width: "80%",
                    }}
                  >
                    {obj.name}
                    {obj.kiss && " (KISS)"}
                  </div>
                  <div
                    style={{
                      marginRight: 5,
                      backgroundColor: "#75CE41",
                      borderRadius: 8,
                      height: 16,
                      width: 16,
                      color: "#000000",
                      fontSize: 20,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      paddingTop: 3,
                    }}
                  >
                    +
                  </div>
                </div>
              ))}
            </div>
          }
        </Styles>
      </div>
      {loading && <Loading message={"Loading..."} />}
    </div>
  );
}

export default React.memo(DashboardTable);
